import {
  FIRST_LAST_NAME,
  LOG_IN_TOKEN,
  REDIRECT_URL,
} from "../../reducers/types";

const knownEnvironments = ['dev', 'cloud-stage']; // List of known environments
const knownTwoLevelTLDs = ['at-hub.com'];

const getBaseDomain = (hostname) => {
  const parts = hostname.split('.');
  const numParts = parts.length;

  if (hostname.includes('localhost')) {
    return 'localhost';
  }

  let tld = '';
  if (numParts >= 2) {
    tld = `${parts[numParts - 2]}.${parts[numParts - 1]}`;
  }

  if (knownTwoLevelTLDs.includes(tld) && numParts >= 3) {
    // Base domain is the last three parts
    return parts.slice(-3).join('.');
  }
  // Base domain is the last two parts
  return parts.slice(-2).join('.');
};

const parseHostname = (hostname) => {
  const parts = hostname.split('.');
  const baseDomain = getBaseDomain(hostname);
  const baseDomainParts = baseDomain.split('.');

  // Calculate index of potential subdomains
  const numParts = parts.length;
  const numBaseDomainParts = baseDomainParts.length;
  let index = numParts - numBaseDomainParts - 1; // Index of the subdomain part(s)

  let environment = '';

  if (index >= 0) {
    const potentialSubdomain = parts[index];
    index--;

    // Check if the potential subdomain is an environment
    if (knownEnvironments.includes(potentialSubdomain)) {
      environment = potentialSubdomain;
    } else if (index >= 0) {
      const potentialEnv = parts[index];
      if (knownEnvironments.includes(potentialEnv)) {
        environment = potentialEnv;
      }
    }
  }

  return { environment, baseDomain };
};

const getProtocol = () => (typeof window !== 'undefined' ? window.location.protocol : 'https:');

const getUrl = (desiredSubdomain) => {
  const protocol = getProtocol();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'zimtra.com';

  if (hostname.includes('localhost')) {
    return 'http://localhost:3000';
  }

  const { environment, baseDomain } = parseHostname(hostname);

  let fullSubdomain = '';

  if (environment && desiredSubdomain) {
    // Both environment and desired subdomain are present
    fullSubdomain = `${environment}.${desiredSubdomain}`;
  } else if (environment) {
    // Only environment is present
    fullSubdomain = `${environment}`;
  } else if (desiredSubdomain) {
    // Only desired subdomain is present
    fullSubdomain = `${desiredSubdomain}`;
  } else {
    // Neither environment nor desired subdomain are present
    fullSubdomain = '';
  }

  // Construct the URL
  if (fullSubdomain) {
    return `${protocol}//${fullSubdomain}.${baseDomain}`;
  }
  return `${protocol}//${baseDomain}`;
};

export const utilityFns = {
  findCurrentTradingPlatform: (profile, rates) => {
    const ratesValues =
      Object.keys(rates).length > 1
        ? rates.tradingplatforms
        : rates.tradingPlatforms;
    const potentialTPS = ratesValues?.filter((tp) =>
      tp.accountTypeRefs.includes(profile.accountType)
    );
  
    let potentialPlatforms = profile.software
      ? potentialTPS.filter((tp) => tp.software === profile.software)
      : potentialTPS;
  
    if (potentialPlatforms.length > 1) {
      potentialPlatforms.forEach((platform) => {
        if (platform.conditionFields) {
          platform.conditionFields.forEach(({ conditions, fields }) => {
            const conditionKey = Object.keys(conditions)[0];
            const conditionValue = conditions[conditionKey];
            const profileValue = profile.agreement?.rules?.[conditionKey];
            if (
              conditionValue &&
              typeof conditionValue === "object" &&
              conditionValue.min !== undefined &&
              conditionValue.max !== undefined &&
              profileValue >= conditionValue.min &&
              profileValue <= conditionValue.max
            ) {
              if (fields.softwareType) {
                profile.softwareType = platform.softwareType;
              }
            }
          });
        }
      });
    }

    let foundPlatform = potentialPlatforms.find((tp, index) => {
      if (
        tp.softwareType === profile.softwareType &&
        tp.accountTypeRefs.includes(profile.accountType)
      ) {
        return true;
      }
      if (
        !profile.software &&
        profile.accountType &&
        profile.accountType === "zimtraexpert" &&
        tp.accountTypeRefs.includes(profile.accountType)
      ) {
        //TODO: remove this after implementing BA for everyone
        if (potentialPlatforms.length > 1) {
          if (index === 1) {
            return true;
          } else {
            return false;
          }
        }
        return true;
      }
      return false;
    });
  
    return foundPlatform ? foundPlatform : null;
  },
  

  updateProfileSoftwareTypeWithConditions: (tradingPlatform, profile) => {
    if (!tradingPlatform.conditionFields || !Array.isArray(tradingPlatform.conditionFields)) {
      return profile; // No conditions, return original profile
    }
  
    let updatedProfile = { ...profile };
  
    tradingPlatform.conditionFields.forEach(({ conditions, fields }) => {
      const conditionKey = Object.keys(conditions)[0];
      const conditionValue = conditions[conditionKey];
  
      // Check if conditionValue is an object with min and max properties
      if (conditionValue && typeof conditionValue === 'object' && conditionValue.min !== undefined && conditionValue.max !== undefined) {
        const profileValue = profile.agreement?.rules?.[conditionKey];
  
        // Check if profileValue is within the range of min and max
        if (profileValue >= conditionValue.min && profileValue <= conditionValue.max) {
          if (fields.softwareType) {
            updatedProfile.softwareType = fields.softwareType; // Update softwareType if condition is met
          }
        }
      }
    });
  
    return updatedProfile;
  },

  calculatePlatformPriceWithConditions: (tradingPlatform, profile) => {
    if (tradingPlatform === null) {
      return undefined;
    }
    if (!tradingPlatform.conditionFields || !Array.isArray(tradingPlatform.conditionFields)) {
      return tradingPlatform.price; // No conditions, return the original price
    }
  
    let updatedPrice = tradingPlatform.price;
  
    tradingPlatform.conditionFields.forEach(({ conditions, fields }) => {
      const conditionKey = Object.keys(conditions)[0];
      const conditionValue = conditions[conditionKey];
  
      // Check if conditionValue is an object with min and max properties
      if (conditionValue && typeof conditionValue === 'object' && conditionValue.min !== undefined && conditionValue.max !== undefined) {
        const profileValue = profile.agreement?.rules?.[conditionKey];
        
        // Check if profileValue is within the range of min and max
        if (profileValue >= conditionValue.min && profileValue < conditionValue.max) {
          if (fields.price) {
            updatedPrice = fields.price; // Update price if condition is met
          }
        }
      } else if (profile[conditionKey] === conditionValue) {
        // Fallback for simple value comparison
        if (fields.price) {
          updatedPrice = fields.price; // Update price if condition is met
        }
      }
    });
  
    return updatedPrice;
  },
  
  validEnglishText: (t) => {
    return (
      (typeof t === "string" && !!t.match(/^[A-Za-z \-.']*$/)) ||
      typeof t === "undefined"
    );
  },
  strToBoolean: (s) => {
    s = String(s).toLowerCase();
    return s === "true" || s === "yes" || s === "1";
  },
  validEnglishTextAndNumber: (t) => {
    return (
      (typeof t === "string" && !!t.match(/^[A-Za-z0-9 \-.']*$/)) ||
      typeof t === "undefined"
    );
  },
  validEmail: (v) => {
    return (
      (typeof v === "string" &&
        !!v.match(
          /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@(([^<>()[\]\\.,;:\s@\\"]+\.)+[^<>()[\]\\.,;:\s@\\"]{2,})$/i
        )) ||
      typeof v === "undefined"
    );
  },
  validatePassword: (t) => {
    return (
      (typeof t === "string" &&
        !!t.match(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,40}$/
        ) &&
        t.slice(0, 1) !== " " &&
        t.slice(-1) !== " ") ||
      typeof t === "undefined"
    );
  },
  phoneNumber: (t) => {
    return (
      (typeof t === "string" && !!t.match(/^(\+[0-9]+)?([0-9\- ]*)$/)) ||
      typeof t === "undefined" ||
      t.length === 0
    );
  },
  validIDNumber: (t) => {
    return typeof t === "string" && !!t.match(/^[A-Za-z0-9.\-#+:]+$/);
  },
  money: (v, frac = 0) => {
    const f = parseFloat(v);
    if (!isNaN(f)) {
      return f.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: frac,
      });
    }
    return v;
  },
  moneyshort: (v, frac = 1, noDollar = false) => {
    return (
      `${noDollar ? "" : "$"}` +
      new Intl.NumberFormat("en-US", {
        maximumFractionDigits: frac,
        notation: "compact",
        compactDisplay: "short",
      }).format(v)
    );
  },
  getMarketId: (m) => {
    return `${m.group}-${m.name}`;
  },
  toFixed: (x, pref, max) => {
    const xmax = x.toFixed(max);
    return xmax.slice(-1) === "0" ? x.toFixed(pref) : xmax;
  },
  emptyString: (v) => {
    return typeof v !== "undefined" && v.trim().length !== 0;
  },
  sortByOrderField: (a, b) => {
    return a.order < b.order ? -1 : a.order === b.order ? 0 : 1;
  },
  showForMeOrRostik: (profile) => {
    return (
      profile?.personal?.email === "kazak13v@gmail.com" ||
      profile?.personal?.email === "Stoxro@gmail.com" ||
      profile?.personal?.email === "r.v.tataryn@gmail.com"
    );
  },
  checkAvailableAllRates: (rates) => {
    return Object.keys(rates).every((r) => rates[r].length > 0);
  },
  getImageFromStore: (imageName, place, year = "2022") => {
    return `${process.env.PUBLIC_URL}/${imageName}`;
  },
  addRemoveScrollEventToBody: (add, _event) => {
    const isMobile = window.innerWidth <= 500;
    const slider = document.getElementById("slide-out-widget-area-bg");
    if (add) {
      document
        .querySelector("body")
        .addEventListener(_event, preventScroll, { passive: false });
      document.querySelector("body").classList.add("hidden-scrollbar");
      if (!isMobile) {
        document
          .querySelector("body")
          .classList.add("no-scrollbar-padding-right");
      }
      if (slider) slider.classList.add("no-scrollbar-set-bg");
    } else {
      document
        .querySelector("body")
        .removeEventListener(_event, preventScroll, { passive: false });
      document.querySelector("body").classList.remove("hidden-scrollbar");
      if (!isMobile) {
        document
          .querySelector("body")
          .classList.remove("no-scrollbar-padding-right");
      }
      if (slider) slider.classList.remove("no-scrollbar-set-bg");
    }
  },
  eraseTokens: () => {
    localStorage.removeItem(REDIRECT_URL)
    localStorage.removeItem(LOG_IN_TOKEN)
    sessionStorage.removeItem(REDIRECT_URL);
    
    localStorage.removeItem(FIRST_LAST_NAME);
  },
  findMatchingBundle: (arr, bundles) => {
    if (!bundles || bundles.length === 0) {
      return false;
    }
    // Helper function to compare two arrays
    const arraysEqualSet = (arr1 = [], arr2 = []) => {
      const sortFn = (a, b) => a.localeCompare(b);
      const sortedArr1 = [...arr1].sort(sortFn);
      const sortedArr2 = [...arr2].sort(sortFn);
      return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
    };
  
    // Iterate through each bundle
    for (const bundle of bundles) {
      if (arraysEqualSet(arr, bundle.pack)) {
        return bundle.bundleName;
      }
    }
    return false;
  },
  getDomainFromHostname: (hostname) => {
    if (hostname.includes('localhost')) {
      return 'localhost';
    }

    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const lastPart = parts[parts.length - 1];
      const secondLastPart = parts[parts.length - 2];
      const sld = `${secondLastPart}.${lastPart}`;

      // List of known two-level TLDs
      const twoLevelTLDs = ['at-hub.com'];

      if (twoLevelTLDs.includes(sld)) {
        // Domain is last 3 parts
        return `.${parts.slice(-3).join('.')}`;
      }
      // Domain is last 2 parts
      return `.${parts.slice(-2).join('.')}`;
    }
    return hostname;
  },
  redirectToLanding: (path) => {
    if (path) {
      window.location.replace(`${getUrl('')}/${path}`);
    } else {
      window.location.replace(getUrl(''));
    }
  },
  getLandingUrl: () => getUrl(''),

  redirectToPortal: () => {
      window.location.replace(getUrl('portal'));
  },

  redirectToPortalWithLandingPageInNewTab: (landingPath) => {
    window.open(getUrl('portal'));
    window.location.replace(`https://zimtra.com/${landingPath}`);
  },

  // GA4 Tracking Utility Functions
  ga4Tracking: {
    // Track custom events
    trackEvent: (eventName, parameters = {}) => {
      if (window.gtag) {
        window.gtag('event', eventName, {
          event_category: parameters.category || 'User Action',
          event_label: parameters.label || eventName,
          // Destructure parameters for better GA4 visibility
          step_name: parameters.step_name,
          step_number: parameters.step_number,
          total_steps: parameters.total_steps,
          user_id: parameters.user_id,
          user_email: parameters.user_email,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          page_url: window.location.href
        });
      }
    },

    // Track Sumsub specific events
    trackSumsubEvent: (eventName, messageName, value, userContext = {}) => {
      if (window.gtag) {
        window.gtag('event', `sumsub_${eventName}`, {
          event_category: 'ID Verification',
          event_label: eventName,
          // Destructure parameters for better GA4 visibility
          message_name: messageName,
          value: JSON.stringify(value),
          user_id: userContext.userId || 'unknown',
          user_email: userContext.userEmail || 'unknown',
          step_number: userContext.stepNumber || 'unknown',
          timestamp: new Date().toISOString()
        });
      }
    },

    // Track user journey events
    trackUserJourney: (stepName, stepNumber, totalSteps, userId, userEmail) => {
      if (window.gtag) {
        window.gtag('event', 'user_journey_step', {
          event_category: 'User Journey',
          event_label: stepName,
          // Destructure parameters for better GA4 visibility
          step_name: stepName,
          step_number: stepNumber,
          total_steps: totalSteps,
          user_id: userId || 'unknown',
          user_email: userEmail || 'unknown',
          progress_percentage: Math.round((stepNumber / totalSteps) * 100)
        });
      }
    },

    // Track conversion events
    trackConversion: (conversionType, value = 0, currency = 'USD', userEmail = null) => {
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'G-Z80CCM70RR',
          value: value,
          currency: currency,
          // Destructure parameters for better GA4 visibility
          conversion_type: conversionType,
          user_email: userEmail,
          timestamp: new Date().toISOString()
        });
      }
    },

    // Set user properties (call this once when user logs in or profile loads)
    setUserProperties: (userId, userEmail) => {
      if (window.gtag) {
        window.gtag('set', 'user_properties', {
          user_id: userId,
          user_email: userEmail
        });
      }
    }
  }
};

const preventScroll = (e) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};
